import { m } from "@recobra/i18n/messages";
import type { NotificationInboxItem } from "@recobra/schemas/notification";

import type { FormattedNotificationContent } from "./formatNotificationContent";
import { caseNumberLabel, stepLabel } from "./formatTaskNotificationContent";

export const formatNoteNotification = (
  item: NotificationInboxItem,
  kind: "note.added" | "note.mention",
): FormattedNotificationContent | null => {
  const params = item.displayParams;
  const caseNumber = caseNumberLabel(item);
  const actor = item.actor?.displayName ?? m.notifications_system_source();
  if (!caseNumber) return null;

  if (kind === "note.added") {
    return {
      title: m.notifications_note_added_title({ caseNumber }),
      message: params?.noteExcerpt ?? m.notifications_note_added_body({ actor, caseNumber }),
      actionLabel: m.notifications_action_view_case(),
    };
  }

  return {
    title: m.notifications_note_mention_title(),
    message: params?.noteExcerpt ?? m.notifications_note_mention_body({ actor, caseNumber }),
    actionLabel: m.notifications_action_view_case(),
  };
};

export const formatCaseStepNotification = (
  item: NotificationInboxItem,
): FormattedNotificationContent | null => {
  const params = item.displayParams;
  const caseNumber = caseNumberLabel(item);
  if (!caseNumber) return null;

  const step = stepLabel(params?.step, item.title);
  const previousStep = stepLabel(params?.previousStep, params?.previousStep ?? "—");

  return {
    title: m.notifications_case_step_changed_title({ caseNumber, step }),
    message: m.notifications_case_step_changed_body({ caseNumber, previousStep, step }),
    actionLabel: m.notifications_action_view_case(),
  };
};

export const formatDocumentNotification = (
  item: NotificationInboxItem,
): FormattedNotificationContent | null => {
  const params = item.displayParams;
  const caseNumber = caseNumberLabel(item);
  if (!caseNumber) return null;

  const document = params?.documentName ?? item.title;

  return {
    title: m.notifications_document_classified_title({ caseNumber }),
    message: m.notifications_document_classified_body({ caseNumber, document }),
    actionLabel: m.notifications_action_view_document(),
  };
};

export const formatDocumentUploadedNotification = (): FormattedNotificationContent => ({
  title: m.notifications_document_uploaded_title(),
  message: m.notifications_document_uploaded_body(),
  actionLabel: m.notifications_action_view_case(),
});

export const formatMessageNotification = (
  item: NotificationInboxItem,
): FormattedNotificationContent => {
  const conversation = item.displayParams?.conversationLabel ?? item.title;

  return {
    title: m.notifications_message_received_title({ conversation }),
    message: m.notifications_message_received_body({ conversation }),
    actionLabel: m.notifications_action_view_conversation(),
  };
};

export const formatSecurityNotification = (
  item: NotificationInboxItem,
  defaultActionLabel: string | undefined,
): FormattedNotificationContent => {
  const params = item.displayParams;
  const location = params?.signInLocation ?? "—";
  const device = params?.signInDevice ?? "—";

  return {
    title: m.notifications_security_sign_in_title(),
    message:
      params?.signInLocation || params?.signInDevice
        ? m.notifications_security_sign_in_body_expanded({ location, device })
        : m.notifications_security_sign_in_body(),
    actionLabel: defaultActionLabel,
  };
};
